import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FrontFirstTreeGroupDto } from '../front-first-permission-tree/front-first-permission-tree.types';

/**
 * Nível 1 do catálogo — GRUPO (ex.: controller/tela "VENDAS", "PRODUTOS").
 * `permissionIds` é a lista COMPLETA de params granulares do grupo (usada para
 * a cascata/tri-state do grupo, independente de como os subgrupos os agrupam).
 * `subgroups` é OPCIONAL: quando ausente, o componente deriva os subgrupos no
 * front pela convenção de verbos CRUD (Ver / Salvar / Excluir / Outras ações),
 * sem exigir backend por tenant (ver spec unified-permission-selector §Subgrupos).
 */
export interface PermissionGroupCatalogEntry {
  id: string;
  name: string;
  permissionIds: string[];
  subgroups?: PermissionSubgroupEntry[];
}

/**
 * Nível 2 do catálogo — SUBGRUPO (ação amigável que embrulha 1..n granulares).
 */
export interface PermissionSubgroupEntry {
  key: string;
  name: string;
  permissionIds: string[];
}

/**
 * Nível 3 — PERMISSÃO granular (system_action_param). O que roda no
 * enforcement de produção; este componente é apenas a camada de organização/UX.
 */
export interface PermissionEntry {
  id: string;
  name: string;
  category?: string | number;
  /**
   * Dica de agrupamento (ex.: nome do Controller, "VENDAS") usada SÓ quando o
   * catálogo agrupado (`catalog`) vem vazio ou sem nenhum grupo com conteúdo —
   * o componente deriva um catálogo sintético a partir das permissões flat
   * (ver {@link deriveControllerGroupLabel}) pra nunca mostrar "Nenhum grupo
   * disponível" quando há permissão disponível. Sem backend de catálogo
   * agrupado (grupos por tenant são fase própria — ver spec §7), este é o
   * jeito de a tela continuar utilizável.
   */
  groupHint?: string;
}

/**
 * Extrai um rótulo de Grupo a partir do nome técnico de uma system_action
 * (convenção `XxxController_metodo` usada pelo AuthorizationInterceptor —
 * `${controllerName}_${methodName}`). Puramente textual, sem depender de
 * backend/catálogo agrupado — usada pelo fallback de agrupamento quando o
 * catálogo do backend vem vazio (bug staging: 1040 permissões flat e "Nenhum
 * grupo de permissões disponível").
 */
export function deriveControllerGroupLabel(rawActionName: string | undefined | null): string {
  const raw = String(rawActionName || '').trim();
  if (!raw) return 'GERAL';
  const controllerPart = raw.split('_')[0]; // "XxxController"
  const withoutSuffix = controllerPart.replace(/Controller$/i, '') || controllerPart;
  // Humaniza PascalCase ("RoomAccessories" -> "Room Accessories") pra ficar
  // legível mesmo sem um mapa de tradução PT específico para o controller.
  const humanized = withoutSuffix.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  return (humanized || raw).toUpperCase();
}

export interface CategoryOption {
  value: string | number;
  label: string;
}

/**
 * 'front-first' = Por tela (área do menu → tela → ação, árvore pronta do wazir-api) ·
 * 'grouped' = Simples (Grupo→Subgrupo) · 'ungrouped' = Avançado (abre granular).
 * Os três operam sobre o MESMO `selectedIds` (ids de `system_action_param`): o que o "Por tela"
 * marca é exatamente o que o Avançado marcaria — nada muda na gravação.
 */
export type PermissionSelectorViewMode = 'front-first' | 'grouped' | 'ungrouped';

/** Rótulos dos subgrupos derivados por convenção de verbo. */
const SUBGROUP_VER = 'Ver';
const SUBGROUP_SALVAR = 'Salvar';
const SUBGROUP_EXCLUIR = 'Excluir';
const WRITE_SUBGROUPS = [SUBGROUP_SALVAR, SUBGROUP_EXCLUIR];

/**
 * Rótulo de folha quando o catálogo (`catalog[].permissionIds`) referencia um id
 * que NÃO está na lista plana `permissions` — ex.: um consumidor monta o catálogo
 * de grupos a partir de um endpoint diferente do que resolve os nomes (achado no
 * bug HOSPEDAGENS/"OUTRAS PERMISSÕES" exibindo UUID cru). NUNCA usar o id como
 * fallback de nome: o correto é o consumidor completar `permissions` com o nome
 * real (join no schema do tenant); isto aqui é só a rede de segurança do
 * componente para nunca vazar um UUID na tela, mesmo se o consumidor falhar.
 */
const UNRESOLVED_PERMISSION_LABEL = 'Permissão não identificada';

/** Grupo já resolvido para render (com subgrupos derivados/aplicados). */
interface ResolvedGroup {
  id: string;
  name: string;
  permissionIds: string[];
  subgroups: ResolvedSubgroup[];
  /**
   * Permissões "soltas": endpoints sem convenção CRUD nem @Access reconhecível.
   * Não entram em subgrupo amigável — aparecem direto sob o grupo, SÓ no Avançado.
   */
  loosePermissions: PermissionEntry[];
}
interface ResolvedSubgroup {
  key: string;
  /** Id do grupo pai — usado para a dependência "escrita auto-inclui Ver". */
  groupId: string;
  name: string;
  permissions: PermissionEntry[];
  permissionIds: string[];
}

@Component({
  selector: 'app-permission-selector',
  templateUrl: './permission-selector.component.html',
  styleUrls: ['./permission-selector.component.sass'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PermissionSelectorComponent implements OnChanges {
  /** Catálogo macro: grupos (opcionalmente com subgrupos). */
  @Input() catalog: PermissionGroupCatalogEntry[] = [];
  /** Lista plana de todas as permissões granulares disponíveis (catálogo micro). */
  @Input() permissions: PermissionEntry[] = [];
  /** IDs dos params atualmente selecionados (two-way via [(selectedIds)]). */
  @Input() selectedIds: string[] = [];
  /** Categorias opcionais para filtro (produto/tipo). */
  @Input() categoryOptions: CategoryOption[] = [];
  /** Desabilita alterações (modo somente leitura). */
  @Input() disabled = false;
  /** Indicador de carregamento do catálogo (spinner opcional). */
  @Input() loading = false;
  /** Força o modo inicial: 'front-first' (Por tela), 'simple' (Grupo→Subgrupo) ou 'advanced' (abre granular). */
  @Input() mode?: 'front-first' | 'simple' | 'advanced';
  /**
   * Árvore "Por tela" (`GET permission-groups/front-first-tree`). Com grupos (ou `frontFirstLoading`)
   * o toggle ganha o modo "Por tela" — que vira o padrão, como no ABCX, enquanto o usuário não
   * escolher outro. Sem árvore (erro/sem permissão/vazia) o seletor segue só Simples/Avançado.
   */
  @Input() frontFirstGroups: FrontFirstTreeGroupDto[] = [];
  @Input() frontFirstLoading = false;

  @Output() selectedIdsChange = new EventEmitter<string[]>();

  viewMode: PermissionSelectorViewMode = 'grouped';
  /** `true` quando o usuário clicou num modo — a partir daí o padrão automático não o sobrescreve. */
  private viewModeChosen = false;
  searchText = '';
  selectedCategory: string | number = 'ALL';

  /**
   * Toggle "Só marcados" (spec §Filtros e limpeza da árvore): filtra a árvore
   * pra mostrar só grupos/subgrupos com ≥1 permissão marcada (inclui parciais)
   * e só as folhas marcadas por baixo. Continua editável — desmarcar um item
   * com o filtro ligado NÃO o remove na hora; ele fica visível e esmaecido
   * (ver {@link isDimmed}) até o filtro ser reaplicado (desligar e ligar de
   * novo recalcula o snapshot).
   */
  onlyMarked = false;
  /** Snapshot dos ids marcados no momento em que "Só marcados" foi ligado. */
  private onlyMarkedSnapshot: Set<string> | null = null;

  private selectedSet = new Set<string>();
  private expandedGroups = new Set<string>();
  private expandedSubgroups = new Set<string>();

  /** Cache do catálogo resolvido (grupos + subgrupos) — recalculado em ngOnChanges. */
  private resolvedGroups: ResolvedGroup[] = [];
  /** Mapa id→PermissionEntry para lookup de rótulos. */
  private permById = new Map<string, PermissionEntry>();
  /** Referências usadas na última resolução (detecção de staleness defensiva). */
  private lastCatalogRef: PermissionGroupCatalogEntry[] | null = null;
  private lastPermsRef: PermissionEntry[] | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedIds']) {
      this.selectedSet = new Set((this.selectedIds || []).map(String));
    }
    if (changes['mode'] && this.mode) {
      this.viewMode =
        this.mode === 'advanced' ? 'ungrouped' : this.mode === 'front-first' ? 'front-first' : 'grouped';
      this.viewModeChosen = true;
    }
    if (changes['frontFirstGroups'] || changes['frontFirstLoading']) {
      this.syncFrontFirstMode();
    }
    if (changes['catalog'] || changes['permissions']) {
      this.rebuildResolvedGroups();
    }
  }

  // ─── Resolução do catálogo (grupos → subgrupos) ────────────────────────────

  private rebuildResolvedGroups(): void {
    this.permById = new Map((this.permissions || []).map((p) => [String(p.id), p]));
    let groups = (this.catalog || []).map((group) => this.resolveGroup(group));

    // Regra de UX (bug staging: 1040 permissões flat + "Nenhum grupo de
    // permissões disponível"): NUNCA mostrar a árvore vazia quando há ≥1
    // permissão disponível. Sem catálogo agrupado do backend (grupos por
    // tenant são fase própria — spec §7) ou com um catálogo cujos grupos não
    // têm nenhum conteúdo (ex.: interseção own-only zerou tudo), deriva um
    // catálogo sintético a partir das próprias permissões flat.
    const hasUsableGroup = groups.some((g) => !this.isEmptyGroup(g));
    if (!hasUsableGroup && (this.permissions || []).length > 0) {
      groups = this.buildFallbackGroups().map((g) => this.resolveGroup(g));
    }

    this.resolvedGroups = groups;
    this.lastCatalogRef = this.catalog;
    this.lastPermsRef = this.permissions;
  }

  /**
   * Deriva um catálogo sintético Grupo→granular a partir de `this.permissions`,
   * agrupando por `groupHint` (fallback "GERAL" quando ausente). Os subgrupos
   * (Ver/Salvar/Excluir/soltas) continuam vindo da convenção de verbo, igual a
   * qualquer outro grupo — reusa o pipeline de resolução existente.
   */
  private buildFallbackGroups(): PermissionGroupCatalogEntry[] {
    const buckets = new Map<string, string[]>();
    for (const p of this.permissions || []) {
      const label = (p.groupHint && p.groupHint.trim()) || 'GERAL';
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label)!.push(String(p.id));
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, permissionIds], i) => ({
        id: `__fallback_group_${i}__`,
        name,
        permissionIds,
      }));
  }

  /**
   * Garante que a árvore resolvida está em dia com os inputs atuais. Defensivo:
   * consumidores que trocam `catalog`/`permissions` por binding disparam
   * ngOnChanges, mas isto cobre também mutações fora do ciclo de change-detection.
   */
  private ensureResolved(): void {
    if (this.catalog !== this.lastCatalogRef || this.permissions !== this.lastPermsRef) {
      this.rebuildResolvedGroups();
    }
  }

  private resolveGroup(group: PermissionGroupCatalogEntry): ResolvedGroup {
    const permissionIds = (group.permissionIds || []).map(String);
    const groupId = String(group.id);
    if (Array.isArray(group.subgroups) && group.subgroups.length > 0) {
      // Subgrupos vindos do backend/catálogo: sem "soltos" (o backend já organizou).
      return {
        id: groupId,
        name: String(group.name || ''),
        permissionIds,
        subgroups: this.applyCatalogSubgroups(groupId, group.subgroups),
        loosePermissions: [],
      };
    }
    const { subgroups, loose } = this.deriveSubgroups(groupId, permissionIds);
    return {
      id: groupId,
      name: String(group.name || ''),
      permissionIds,
      subgroups,
      loosePermissions: loose,
    };
  }

  /**
   * Subgrupos vindos do backend/catálogo: só resolve os rótulos granulares.
   * Subgrupo sem nenhuma permissão NUNCA é incluído na árvore resolvida (spec
   * §Filtros e limpeza: "não exibir... subgrupo sem permissão") — filtrado
   * aqui na construção, não só na hora de renderizar.
   */
  private applyCatalogSubgroups(groupId: string, subgroups: PermissionSubgroupEntry[]): ResolvedSubgroup[] {
    return subgroups
      .map((sub) => {
        const ids = (sub.permissionIds || []).map(String);
        return {
          key: String(sub.key),
          groupId,
          name: String(sub.name || ''),
          permissionIds: ids,
          permissions: ids.map((id) => this.resolvePermissionEntry(id)),
        };
      })
      .filter((sub) => sub.permissionIds.length > 0);
  }

  /**
   * Resolve o `PermissionEntry` de um id do catálogo. Nunca devolve o id como
   * nome — ver {@link UNRESOLVED_PERMISSION_LABEL}. Quando o id não está na
   * lista `permissions` fornecida pelo consumidor, avisa no console (dev) para
   * facilitar detectar a causa real (catálogo incompleto) em vez de mascarar.
   */
  private resolvePermissionEntry(id: string): PermissionEntry {
    const found = this.permById.get(id);
    if (found) return found;
    // eslint-disable-next-line no-console
    console.warn(
      `[app-permission-selector] permissão "${id}" referenciada pelo catálogo mas ausente do input ` +
      `[permissions] — o consumidor deve incluir o nome real (join no schema do tenant). Exibindo fallback.`,
    );
    return { id, name: UNRESOLVED_PERMISSION_LABEL };
  }

  /**
   * Deriva subgrupos amigáveis pela convenção de verbos CRUD (spec §Subgrupos):
   * find/listar → "Ver"; create+update/salvar/editar → "Salvar";
   * remove/excluir → "Excluir". Endpoints sem convenção reconhecível NÃO viram
   * subgrupo: retornam como permissões "soltas" (loose), visíveis só no Avançado.
   * Não exige backend por tenant — puramente front, aditivo.
   */
  private deriveSubgroups(
    groupId: string,
    permissionIds: string[],
  ): { subgroups: ResolvedSubgroup[]; loose: PermissionEntry[] } {
    const buckets: Record<string, PermissionEntry[]> = {
      [SUBGROUP_VER]: [],
      [SUBGROUP_SALVAR]: [],
      [SUBGROUP_EXCLUIR]: [],
    };
    const loose: PermissionEntry[] = [];
    for (const id of permissionIds) {
      const perm = this.resolvePermissionEntry(id);
      const label = this.classifyByVerb(perm.name);
      if (label === null) loose.push(perm);
      else buckets[label].push(perm);
    }
    const order = [SUBGROUP_VER, SUBGROUP_SALVAR, SUBGROUP_EXCLUIR];
    const subgroups = order
      .filter((label) => buckets[label].length > 0)
      .map((label) => ({
        key: `${groupId}::${label}`,
        groupId,
        name: label,
        permissions: buckets[label],
        permissionIds: buckets[label].map((p) => String(p.id)),
      }));
    return { subgroups, loose };
  }

  /**
   * Classifica pela convenção de verbo. Retorna `null` quando não há convenção
   * CRUD reconhecível — nesse caso o endpoint vira granular solto (loose).
   */
  private classifyByVerb(name: string): 'Ver' | 'Salvar' | 'Excluir' | null {
    const n = this.normalizeStr(name);
    const has = (words: string[]) => words.some((w) => n.includes(w));
    if (has(['excluir', 'remover', 'deletar', 'apagar', 'cancelar', 'remove', 'delete', 'destroy'])) {
      return SUBGROUP_EXCLUIR;
    }
    if (has(['criar', 'cadastrar', 'adicionar', 'editar', 'atualizar', 'salvar', 'alterar', 'gerar', 'nova', 'novo', 'create', 'update', 'store', 'save', 'edit'])) {
      return SUBGROUP_SALVAR;
    }
    if (has(['listar', 'consultar', 'visualizar', 'buscar', 'obter', 'exibir', 'relatorio', 'menu', 'find', 'list', 'show', 'index', 'get', 'read', 'ver'])) {
      return SUBGROUP_VER;
    }
    return null;
  }

  // ─── View mode ─────────────────────────────────────────────────────────────

  setViewMode(mode: PermissionSelectorViewMode): void {
    this.viewMode = mode;
    this.viewModeChosen = true;
    this.cdr.markForCheck();
  }

  /** O modo "Por tela" só existe com árvore disponível (ou carregando). */
  get hasFrontFirst(): boolean {
    return this.frontFirstLoading || (this.frontFirstGroups || []).length > 0;
  }

  get isFrontFirst(): boolean {
    return this.viewMode === 'front-first' && this.hasFrontFirst;
  }

  /**
   * Padrão automático: árvore disponível e nenhum modo escolhido/forçado → "Por tela" (igual ao
   * ABCX). Se a árvore some (erro/sem permissão) estando em "Por tela", volta ao Simples — nunca
   * deixa o seletor vazio.
   */
  private syncFrontFirstMode(): void {
    if (this.hasFrontFirst) {
      if (!this.viewModeChosen) this.viewMode = 'front-first';
    } else if (this.viewMode === 'front-first') {
      this.viewMode = 'grouped';
    }
  }

  /** Seleção vinda da árvore "Por tela" — mesma saída do Simples/Avançado (mesmos ids). */
  onFrontFirstSelectedIdsChange(ids: string[]): void {
    if (this.disabled) return;
    this.applyChange(new Set((ids || []).map(String)));
  }

  get isAdvanced(): boolean {
    return this.viewMode === 'ungrouped';
  }

  // ─── Grupos (nível 1) ──────────────────────────────────────────────────────

  /**
   * Grupos visíveis: nunca inclui um grupo estruturalmente vazio (sem
   * subgrupo e sem permissão solta — spec §Filtros e limpeza: "não exibir
   * grupo sem subgrupo"), independente de busca/toggle. Com "Só marcados"
   * ligado, só passam grupos com ≥1 id no snapshot (cheio ou parcial).
   */
  get visibleGroups(): ResolvedGroup[] {
    this.ensureResolved();
    const term = this.normalizeStr(this.searchText);
    return this.resolvedGroups.filter((group) => {
      if (this.isEmptyGroup(group)) return false;
      if (this.onlyMarked && !this.groupHasMarkedContent(group)) return false;
      if (this.groupNameMatches(group, term)) return true;
      if (this.getVisibleSubgroups(group).length > 0) return true;
      return this.getVisibleLoose(group).length > 0;
    });
  }

  /** Grupo sem NENHUM subgrupo e sem NENHUMA permissão solta — nunca renderiza. */
  private isEmptyGroup(group: ResolvedGroup): boolean {
    return group.subgroups.length === 0 && group.loosePermissions.length === 0;
  }

  /** True se algum id do grupo (em subgrupo ou solto) está no snapshot "Só marcados". */
  private groupHasMarkedContent(group: ResolvedGroup): boolean {
    if (!this.onlyMarkedSnapshot) return false;
    const snap = this.onlyMarkedSnapshot;
    const inSub = group.subgroups.some((s) => s.permissionIds.some((id) => snap.has(String(id))));
    if (inSub) return true;
    return group.loosePermissions.some((p) => snap.has(String(p.id)));
  }

  /**
   * Permissões "soltas" visíveis (busca + categoria + "Só marcados") —
   * renderizadas só no Avançado. Com o toggle ligado, só as que estão no
   * snapshot aparecem (mesmo se já desmarcadas — ver {@link isDimmed}).
   */
  getVisibleLoose(group: ResolvedGroup): PermissionEntry[] {
    const term = this.normalizeStr(this.searchText);
    return (group.loosePermissions || []).filter((p) => {
      if (!this.matchesCategory(p)) return false;
      if (this.onlyMarked && !this.onlyMarkedSnapshot?.has(String(p.id))) return false;
      if (!term) return true;
      return (
        this.normalizeStr(p.name).includes(term) ||
        this.normalizeStr(String(p.id)).includes(term)
      );
    });
  }

  private groupNameMatches(group: ResolvedGroup, term: string): boolean {
    if (!term) return true;
    return this.normalizeStr(group.name).includes(term);
  }

  isGroupChecked(group: { permissionIds: string[] }): boolean {
    const ids = (group.permissionIds || []).map(String);
    return ids.length > 0 && ids.every((id) => this.selectedSet.has(id));
  }

  isGroupIndeterminate(group: { permissionIds: string[] }): boolean {
    if (this.isGroupChecked(group)) return false;
    return (group.permissionIds || []).map(String).some((id) => this.selectedSet.has(id));
  }

  toggleGroup(group: { permissionIds: string[] }, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    const ids = (group.permissionIds || []).map(String);
    if (checked) ids.forEach((id) => next.add(id));
    else ids.forEach((id) => next.delete(id));
    this.applyChange(next);
  }

  // ─── Subgrupos (nível 2) ───────────────────────────────────────────────────

  /**
   * Subgrupos visíveis. Nunca inclui um subgrupo sem permissão (garantido na
   * construção — {@link applyCatalogSubgroups}/{@link deriveSubgroups} já não
   * criam subgrupo vazio). Com "Só marcados" ligado, só passam subgrupos com
   * ≥1 id no snapshot.
   */
  getVisibleSubgroups(group: ResolvedGroup): ResolvedSubgroup[] {
    const term = this.normalizeStr(this.searchText);
    return group.subgroups.filter((sub) => {
      if (sub.permissionIds.length === 0) return false;
      if (this.onlyMarked && !this.subgroupHasMarkedContent(sub)) return false;
      if (term && this.normalizeStr(sub.name).includes(term)) return true;
      return this.getVisiblePermissions(sub).length > 0;
    });
  }

  /** True se algum id do subgrupo está no snapshot "Só marcados". */
  private subgroupHasMarkedContent(sub: ResolvedSubgroup): boolean {
    if (!this.onlyMarkedSnapshot) return false;
    return sub.permissionIds.some((id) => this.onlyMarkedSnapshot!.has(String(id)));
  }

  isSubgroupChecked(sub: ResolvedSubgroup): boolean {
    const ids = sub.permissionIds.map(String);
    return ids.length > 0 && ids.every((id) => this.selectedSet.has(id));
  }

  isSubgroupIndeterminate(sub: ResolvedSubgroup): boolean {
    if (this.isSubgroupChecked(sub)) return false;
    return sub.permissionIds.some((id) => this.selectedSet.has(String(id)));
  }

  toggleSubgroup(sub: ResolvedSubgroup, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    if (checked) {
      sub.permissionIds.forEach((id) => next.add(String(id)));
      // Dependência: marcar um subgrupo de ESCRITA (Salvar/Excluir) auto-inclui
      // o "Ver" da mesma tela (não se escreve sem poder ver/abrir o menu).
      if (WRITE_SUBGROUPS.includes(sub.name)) {
        this.getVerSubgroupIds(sub.groupId).forEach((id) => next.add(String(id)));
      }
    } else {
      sub.permissionIds.forEach((id) => next.delete(String(id)));
    }
    this.applyChange(next);
  }

  /** Ids do subgrupo "Ver" do grupo (para a dependência de escrita→leitura). */
  private getVerSubgroupIds(groupId: string): string[] {
    const group = this.resolvedGroups.find((g) => g.id === groupId);
    const ver = group?.subgroups.find((s) => s.name === SUBGROUP_VER);
    return ver ? ver.permissionIds : [];
  }

  // ─── Permissão granular (nível 3) ──────────────────────────────────────────

  /** Permissões visíveis de um subgrupo (aplica busca + categoria + "Só marcados"). */
  getVisiblePermissions(sub: ResolvedSubgroup): PermissionEntry[] {
    const term = this.normalizeStr(this.searchText);
    return sub.permissions.filter((p) => {
      if (!this.matchesCategory(p)) return false;
      if (this.onlyMarked && !this.onlyMarkedSnapshot?.has(String(p.id))) return false;
      if (!term) return true;
      return (
        this.normalizeStr(p.name).includes(term) ||
        this.normalizeStr(String(p.id)).includes(term)
      );
    });
  }

  isSelected(id: string): boolean {
    return this.selectedSet.has(String(id));
  }

  togglePermission(id: string, checked: boolean): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    if (checked) next.add(String(id));
    else next.delete(String(id));
    this.applyChange(next);
  }

  // ─── "Só marcados" ─────────────────────────────────────────────────────────

  /**
   * Liga/desliga o filtro "Só marcados". Ao LIGAR, captura um snapshot dos ids
   * atualmente selecionados — a árvore filtrada usa esse snapshot (não o
   * estado live), então desmarcar um item com o filtro ligado não o remove na
   * hora (ver {@link isDimmed}); só some quando o filtro é desligado e ligado
   * de novo (reaplica, recalculando o snapshot).
   */
  toggleOnlyMarked(): void {
    this.onlyMarked = !this.onlyMarked;
    this.onlyMarkedSnapshot = this.onlyMarked ? new Set(this.selectedSet) : null;
    this.cdr.markForCheck();
  }

  /**
   * Um item aparece esmaecido quando está visível só por estar no snapshot
   * (o usuário desmarcou depois de ligar o filtro), não por estar marcado agora.
   */
  isDimmed(id: string): boolean {
    return (
      this.onlyMarked &&
      !!this.onlyMarkedSnapshot?.has(String(id)) &&
      !this.isSelected(id)
    );
  }

  /** True quando "Só marcados" está ligado e nada estava selecionado ao ligar. */
  get onlyMarkedEmptySnapshot(): boolean {
    return this.onlyMarked && (!this.onlyMarkedSnapshot || this.onlyMarkedSnapshot.size === 0);
  }

  // ─── Filtro de categoria ───────────────────────────────────────────────────

  private matchesCategory(p: PermissionEntry): boolean {
    if (this.selectedCategory === 'ALL') return true;
    return String(p.category) === String(this.selectedCategory);
  }

  /** Getter de compatibilidade: lista plana filtrada por busca + categoria. */
  get filteredPermissions(): PermissionEntry[] {
    const term = this.normalizeStr(this.searchText);
    return (this.permissions || []).filter((p) => {
      if (!this.matchesCategory(p)) return false;
      if (!term) return true;
      return this.normalizeStr(p.name).includes(term);
    });
  }

  // ─── Contagens ─────────────────────────────────────────────────────────────

  get selectedCount(): number {
    return this.selectedSet.size;
  }

  get totalCount(): number {
    return this.permissions.length;
  }

  get isAllVisibleSelected(): boolean {
    const visible = this.collectVisiblePermissionIds();
    return visible.length > 0 && visible.every((id) => this.selectedSet.has(id));
  }

  get hasExpandedGroups(): boolean {
    return this.expandedGroups.size > 0 || this.expandedSubgroups.size > 0;
  }

  // ─── Ações em massa ───────────────────────────────────────────────────────

  selectAllVisible(): void {
    if (this.disabled) return;
    const next = new Set(this.selectedSet);
    this.collectVisiblePermissionIds().forEach((id) => next.add(id));
    this.applyChange(next);
  }

  clearAllVisible(): void {
    if (this.disabled) return;
    const toRemove = new Set(this.collectVisiblePermissionIds());
    const next = new Set([...this.selectedSet].filter((id) => !toRemove.has(id)));
    this.applyChange(next);
  }

  toggleAllVisible(checked: boolean): void {
    if (checked) this.selectAllVisible();
    else this.clearAllVisible();
  }

  toggleBulkExpansion(): void {
    if (this.hasExpandedGroups) this.collapseAll();
    else this.expandAll();
  }

  expandAll(): void {
    this.ensureResolved();
    this.resolvedGroups.forEach((g) => {
      this.expandedGroups.add(g.id);
      g.subgroups.forEach((s) => this.expandedSubgroups.add(s.key));
    });
    this.cdr.markForCheck();
  }

  collapseAll(): void {
    this.expandedGroups.clear();
    this.expandedSubgroups.clear();
    this.cdr.markForCheck();
  }

  // ─── Expandir/recolher ─────────────────────────────────────────────────────

  isExpanded(groupId: string): boolean {
    return this.expandedGroups.has(groupId);
  }

  toggleExpand(groupId: string): void {
    if (this.expandedGroups.has(groupId)) this.expandedGroups.delete(groupId);
    else this.expandedGroups.add(groupId);
    this.cdr.markForCheck();
  }

  isSubgroupExpanded(key: string): boolean {
    return this.expandedSubgroups.has(key);
  }

  toggleSubgroupExpand(key: string): void {
    if (this.expandedSubgroups.has(key)) this.expandedSubgroups.delete(key);
    else this.expandedSubgroups.add(key);
    this.cdr.markForCheck();
  }

  // ─── Compat: getters usados por specs/consumidores antigos ─────────────────

  /** Permissões (achatadas) de um grupo do catálogo — compat com API antiga. */
  getGroupPermissions(group: { permissionIds: string[] }): PermissionEntry[] {
    const ids = new Set((group.permissionIds || []).map(String));
    return (this.permissions || []).filter((p) => ids.has(String(p.id)));
  }

  // ─── Utilitários ───────────────────────────────────────────────────────────

  private collectVisiblePermissionIds(): string[] {
    const ids = new Set<string>();
    for (const group of this.visibleGroups) {
      for (const sub of this.getVisibleSubgroups(group)) {
        for (const perm of this.getVisiblePermissions(sub)) {
          ids.add(String(perm.id));
        }
      }
      for (const perm of this.getVisibleLoose(group)) {
        ids.add(String(perm.id));
      }
    }
    return [...ids];
  }

  private applyChange(next: Set<string>): void {
    this.selectedSet = next;
    this.selectedIdsChange.emit([...next]);
    this.cdr.markForCheck();
  }

  private normalizeStr(str: string): string {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  trackByGroupId(_i: number, g: ResolvedGroup): string {
    return g.id;
  }

  trackBySubKey(_i: number, s: ResolvedSubgroup): string {
    return s.key;
  }

  trackByPermId(_i: number, p: PermissionEntry): string {
    return String(p.id);
  }
}
